import { useRuntimeConfig } from '#imports'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { MarketWithSubMarkets } from '@backmarket/http-api/src/api-specs-shipping/rules'
import { hashObjects } from '@backmarket/utils/object/hashObjects'

import type {
  GetShippingValidationThresholdsResponse,
  PriceThreshold,
} from '~/api/shipping/thresholds.types'

import { intersect } from '../../../../Shipping.utils'
import type {
  ShippingMethodsMetadata,
  ShippingRuleMethod,
} from '../../CategoryList.types'

import { DIFFERENCE_PAID_VS_FREE_SHIPPING } from './RuleDrawer.constants'
import type {
  FormMethodValues,
  FormValues,
  ShippingValidationThresholds,
} from './RuleDrawer.types'

export function transformShippingValidationThresholds(
  validationThresholds: GetShippingValidationThresholdsResponse,
): ShippingValidationThresholds {
  const hasNewThresholdFormat =
    useRuntimeConfig().public.FF_ENABLE_NEW_SHIPPING_THRESHOLD_FORMAT

  const thresholds: ShippingValidationThresholds = {
    prices: { max: -1 },
    pricesByCurrency: {},
    shippingTimes: { values: [], minPromised: -1 },
    methods: { free: { min: -1 } },
  }

  validationThresholds.thresholdKinds.forEach((group) => {
    group.thresholds.forEach((threshold) => {
      if (threshold.thresholdType === 'MAX_SHIPPING_FEES') {
        if (hasNewThresholdFormat) {
          thresholds.pricesByCurrency = Object.fromEntries(
            Array.isArray(threshold.threshold)
              ? (threshold.threshold as PriceThreshold[]).map(
                  (thresholdPrice) => [
                    thresholdPrice.currency,
                    { max: parseFloat(thresholdPrice.amount) },
                  ],
                )
              : [],
          )
        } else {
          thresholds.prices.max = parseFloat(threshold.threshold as string)
        }
      } else if (threshold.thresholdType === 'MAX_FREE_TRANSIT_TIME') {
        thresholds.shippingTimes.values = threshold.threshold as number[]
      } else if (threshold.thresholdType === 'MINIMUM_PROMISED_TRANSIT_TIME') {
        thresholds.shippingTimes.minPromised = parseFloat(
          threshold.threshold as string,
        )
      } else if (threshold.thresholdType === 'FREE_METHODS_MIN_COUNT') {
        thresholds.methods.free.min = parseFloat(threshold.threshold as string)
      }
    })
  })

  return thresholds
}

export function getCurrentMethodsKey(isFree: boolean): keyof FormValues {
  return isFree ? 'freeMethods' : 'paidMethods'
}

export function formatMethod(method: FormMethodValues | ShippingRuleMethod) {
  const formattedMethod = {
    ...method,
    id: undefined,
    key: undefined,
    price: +method.price,
    markets: method.markets
      .slice()
      .sort((a: MarketWithSubMarkets, b: MarketWithSubMarkets) => {
        return a.market.localeCompare(b.market)
      }),
    shippingTime: method.shippingTime.toString(),
  }

  return {
    ...formattedMethod,
    id: method.id,
    key: hashObjects(formattedMethod),
  }
}

export function getMinShippingTime(
  methods: FormMethodValues[],
  targetedMarkets: string[] | MarketCountryCode[],
): null | number {
  let minimumShippingTime: number | null = null

  // the collection point methods are not taken into account for the min free shipping time
  methods
    .filter(({ isCollectionPoint }) => !isCollectionPoint)
    .forEach((method) => {
      const flattenedMarketAndSubMarketsCodes = method.markets
        .map(({ market, subMarkets }) =>
          subMarkets.length > 0
            ? subMarkets.map(({ subMarketCode }) => subMarketCode)
            : market,
        )
        .flat()

      if (
        intersect(flattenedMarketAndSubMarketsCodes, targetedMarkets).length
      ) {
        if (
          !minimumShippingTime ||
          Number(method.shippingTime) < minimumShippingTime
        ) {
          minimumShippingTime = Number(method.shippingTime)
        }
      }
    })

  return minimumShippingTime
}

export function isPaidMethodTimeFastEnough(
  time: number,
  minFreeShippingTime: number | null,
) {
  return (
    minFreeShippingTime &&
    time <= minFreeShippingTime - DIFFERENCE_PAID_VS_FREE_SHIPPING
  )
}

export function isMethodValid(
  method: FormMethodValues,
  currentMethods: FormMethodValues[],
  otherMethods: FormMethodValues[],
  isFree: boolean,
  isFreeMandatory: boolean,
  expectedCount = 0,
) {
  const methodKey = formatMethod(method).key

  const isValid = !!(
    method.shippingProvider &&
    method.shippingTime &&
    currentMethods.filter(({ key }) => key === methodKey).length ===
      expectedCount &&
    !otherMethods.some(({ key }) => key === methodKey)
  )

  if (isFree || !isFreeMandatory) {
    return isValid
  }

  const minFreeShippingTime = getMinShippingTime(
    otherMethods,
    method.markets
      .map(({ market, subMarkets }) =>
        subMarkets.length > 0
          ? subMarkets.map(({ subMarketCode }) => subMarketCode)
          : market,
      )
      .flat(),
  )

  return (
    isValid &&
    method.price &&
    isPaidMethodTimeFastEnough(+method.shippingTime, minFreeShippingTime)
  )
}

/**
 * Determines if the collection point shipping option should be displayed to the seller.
 *
 * The API returns carriers that are available in the seller's origin country.
 * Collection point availability is determined by checking if any of the returned
 * carriers support collection point delivery (carrier.collectionPoint = true).
 */
export function shouldShowCollectionPointBlock({
  carriers,
}: {
  carriers: ShippingMethodsMetadata['providerOptions']
}): boolean {
  const collectionPointCarriers = carriers.filter(
    ({ carrier }) => carrier.collectionPoint,
  )

  return collectionPointCarriers.length > 0
}

export function getAvailableCollectionPointCarriers(
  carriers: ShippingMethodsMetadata['providerOptions'],
): MarketWithSubMarkets[] {
  const collectionPointCarriers = carriers.filter(
    ({ carrier }) => carrier.collectionPoint,
  )

  return collectionPointCarriers.flatMap(({ carrier }) => {
    return carrier.restrictedToMarkets?.map((market) => {
      return {
        market,
        subMarkets: [],
      }
    })
  }) as MarketWithSubMarkets[]
}
